import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useSearchParams } from 'react-router-dom';
import { 
  updateProfile, 
  getPaymentMethods, 
  addPaymentMethod, 
  deletePaymentMethod, 
  setDefaultPayment,
  getSocialConnections,
  connectSocialAccount,
  disconnectSocialAccount,
  startLinkedInOAuth
} from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { 
  User, 
  CreditCard, 
  Plus, 
  Trash2,
  Check,
  Save,
  Link2,
  Unlink,
  Loader2,
  ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';

const SOCIAL_PLATFORMS = [
  { id: 'linkedin', name: 'LinkedIn', color: 'bg-blue-700', icon: 'in', oauth: true },
  { id: 'google', name: 'Google', color: 'bg-red-500', icon: 'G', oauth: false },
];

export default function SettingsPage() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [profile, setProfile] = useState({
    name: '',
    email: '',
    phone: '',
    company: ''
  });
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [socialConnections, setSocialConnections] = useState([]);
  const [loading, setLoading] = useState(false);
  const [linkedInLoading, setLinkedInLoading] = useState(false);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [socialDialogOpen, setSocialDialogOpen] = useState(false);
  const [selectedPlatform, setSelectedPlatform] = useState(null);
  const [newPayment, setNewPayment] = useState({
    card_type: 'visa',
    last_four: '',
    expiry_month: '',
    expiry_year: '',
    cardholder_name: ''
  });
  const [socialForm, setSocialForm] = useState({
    platform_username: '',
    platform_email: ''
  });

  // Handle OAuth callback results
  useEffect(() => {
    const linkedinStatus = searchParams.get('linkedin');
    if (linkedinStatus) {
      if (linkedinStatus === 'success') {
        toast.success('LinkedIn account connected successfully!');
        fetchSocialConnections();
      } else if (linkedinStatus === 'denied') {
        toast.error('LinkedIn connection was cancelled');
      } else if (linkedinStatus === 'already_linked') {
        toast.error('This LinkedIn account is already linked to another user');
      } else if (linkedinStatus === 'error') {
        toast.error('Failed to connect LinkedIn account. Please try again.');
      }
      // Clear the query params
      setSearchParams({});
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (user) {
      setProfile({
        name: user.name || '',
        email: user.email || '',
        phone: user.phone || '',
        company: user.company || ''
      });
    }
    fetchPaymentMethods();
    fetchSocialConnections();
  }, [user]);

  const fetchPaymentMethods = async () => {
    try {
      const response = await getPaymentMethods();
      setPaymentMethods(response.data);
    } catch (error) {
      console.error('Failed to fetch payment methods');
    }
  };

  const fetchSocialConnections = async () => {
    try {
      const response = await getSocialConnections();
      setSocialConnections(response.data);
    } catch (error) {
      console.error('Failed to fetch social connections');
    }
  };

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await updateProfile(profile);
      toast.success('Profile updated successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handleAddPayment = async (e) => {
    e.preventDefault();
    try {
      await addPaymentMethod(newPayment);
      toast.success('Payment method added');
      setPaymentDialogOpen(false);
      setNewPayment({
        card_type: 'visa',
        last_four: '',
        expiry_month: '',
        expiry_year: '',
        cardholder_name: ''
      });
      fetchPaymentMethods();
    } catch (error) {
      toast.error('Failed to add payment method');
    }
  };

  const handleDeletePayment = async (methodId) => {
    if (!window.confirm('Remove this payment method?')) return;
    try {
      await deletePaymentMethod(methodId);
      toast.success('Payment method removed');
      fetchPaymentMethods();
    } catch (error) {
      toast.error('Failed to remove payment method');
    }
  };

  const handleSetDefault = async (methodId) => {
    try {
      await setDefaultPayment(methodId);
      toast.success('Default payment method updated');
      fetchPaymentMethods();
    } catch (error) {
      toast.error('Failed to update default');
    }
  };

  const handleConnectSocial = async (e) => {
    e.preventDefault();
    if (!selectedPlatform) return;
    
    try {
      await connectSocialAccount(selectedPlatform.id, socialForm);
      toast.success(`${selectedPlatform.name} account connected`);
      setSocialDialogOpen(false);
      setSocialForm({ platform_username: '', platform_email: '' });
      setSelectedPlatform(null);
      fetchSocialConnections();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to connect account');
    }
  };

  const handleLinkedInConnect = async () => {
    setLinkedInLoading(true);
    try {
      const response = await startLinkedInOAuth();
      // Redirect to LinkedIn OAuth
      window.location.href = response.data.auth_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start LinkedIn connection');
      setLinkedInLoading(false);
    }
  };

  const handleDisconnectSocial = async (platform) => {
    if (!window.confirm(`Disconnect ${platform} account?`)) return;
    try {
      await disconnectSocialAccount(platform);
      toast.success('Account disconnected');
      fetchSocialConnections();
    } catch (error) {
      toast.error('Failed to disconnect account');
    }
  };

  const getCardIcon = (type) => {
    const colors = {
      visa: 'text-blue-400',
      mastercard: 'text-orange-400',
      amex: 'text-green-400',
      discover: 'text-purple-400'
    };
    return <CreditCard className={`w-6 h-6 ${colors[type] || 'text-slate-400'}`} />;
  };

  const isConnected = (platformId) => {
    return socialConnections.find(c => c.platform === platformId);
  };

  return (
    <div className="space-y-8 max-w-4xl" data-testid="settings-page">
      <div>
        <h1 className="text-3xl font-bold text-white mb-2">Settings</h1>
        <p className="text-slate-400">Manage your account, payments, and social connections</p>
      </div>

      {/* Profile Settings */}
      <Card className="bg-[#111827] border-white/10">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <User className="w-5 h-5 text-[#4A90D9]" />
            Profile Information
          </CardTitle>
          <CardDescription className="text-slate-400">
            Update your account details
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleProfileUpdate} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-300">Full Name</Label>
                <Input
                  value={profile.name}
                  onChange={(e) => setProfile({ ...profile, name: e.target.value })}
                  className="mt-1 bg-[#1E293B] border-slate-700 text-white"
                  data-testid="settings-name"
                />
              </div>
              <div>
                <Label className="text-slate-300">Email Address</Label>
                <Input
                  type="email"
                  value={profile.email}
                  onChange={(e) => setProfile({ ...profile, email: e.target.value })}
                  className="mt-1 bg-[#1E293B] border-slate-700 text-white"
                  data-testid="settings-email"
                />
              </div>
              <div>
                <Label className="text-slate-300">Phone Number</Label>
                <Input
                  value={profile.phone}
                  onChange={(e) => setProfile({ ...profile, phone: e.target.value })}
                  placeholder="+1 (555) 000-0000"
                  className="mt-1 bg-[#1E293B] border-slate-700 text-white"
                  data-testid="settings-phone"
                />
              </div>
              <div>
                <Label className="text-slate-300">Company</Label>
                <Input
                  value={profile.company}
                  onChange={(e) => setProfile({ ...profile, company: e.target.value })}
                  className="mt-1 bg-[#1E293B] border-slate-700 text-white"
                  data-testid="settings-company"
                />
              </div>
            </div>
            <div className="pt-4">
              <Button 
                type="submit" 
                disabled={loading}
                className="bg-[#4A90D9] hover:bg-[#3A80C9]"
                data-testid="save-profile-btn"
              >
                {loading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Save Changes
                  </>
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Social Media Connections */}
      <Card className="bg-[#111827] border-white/10">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Link2 className="w-5 h-5 text-[#4A90D9]" />
            Social Media Connections
          </CardTitle>
          <CardDescription className="text-slate-400">
            Connect your social media accounts for seamless integration
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {SOCIAL_PLATFORMS.map((platform) => {
              const connection = isConnected(platform.id);
              return (
                <div 
                  key={platform.id}
                  className="flex items-center justify-between p-4 rounded-lg bg-white/5 border border-white/10"
                  data-testid={`social-${platform.id}`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-lg ${platform.color} flex items-center justify-center text-white font-bold text-sm`}>
                      {platform.icon}
                    </div>
                    <div>
                      <p className="text-white font-medium">{platform.name}</p>
                      {connection ? (
                        <p className="text-sm text-green-400">
                          {connection.platform_username ? `@${connection.platform_username}` : 'Connected'}
                        </p>
                      ) : (
                        <p className="text-sm text-slate-500">Not connected</p>
                      )}
                    </div>
                  </div>
                  {connection ? (
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => handleDisconnectSocial(platform.id)}
                      className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                      data-testid={`disconnect-${platform.id}`}
                    >
                      <Unlink className="w-4 h-4 mr-1" />
                      Disconnect
                    </Button>
                  ) : platform.oauth ? (
                    // OAuth-based connection (LinkedIn)
                    <Button 
                      variant="outline"
                      size="sm"
                      onClick={handleLinkedInConnect}
                      disabled={linkedInLoading}
                      className="border-slate-600 text-slate-300 hover:bg-white/10"
                      data-testid={`connect-${platform.id}`}
                    >
                      {linkedInLoading ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                          Connecting...
                        </>
                      ) : (
                        <>
                          <ExternalLink className="w-4 h-4 mr-1" />
                          Connect
                        </>
                      )}
                    </Button>
                  ) : (
                    // Manual connection (Google - placeholder)
                    <Button 
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setSelectedPlatform(platform);
                        setSocialDialogOpen(true);
                      }}
                      className="border-slate-600 text-slate-300 hover:bg-white/10"
                      data-testid={`connect-${platform.id}`}
                    >
                      <Link2 className="w-4 h-4 mr-1" />
                      Connect
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Social Connect Dialog */}
      <Dialog open={socialDialogOpen} onOpenChange={setSocialDialogOpen}>
        <DialogContent className="bg-[#111827] border-white/10">
          <DialogHeader>
            <DialogTitle className="text-white">
              Connect {selectedPlatform?.name}
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Enter your {selectedPlatform?.name} account details
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleConnectSocial} className="space-y-4">
            <div>
              <Label className="text-slate-300">Username *</Label>
              <Input
                value={socialForm.platform_username}
                onChange={(e) => setSocialForm({ ...socialForm, platform_username: e.target.value })}
                placeholder={`Your ${selectedPlatform?.name} username`}
                required
                className="mt-1 bg-[#1E293B] border-slate-700 text-white"
                data-testid="social-username"
              />
            </div>
            <div>
              <Label className="text-slate-300">Email (optional)</Label>
              <Input
                type="email"
                value={socialForm.platform_email}
                onChange={(e) => setSocialForm({ ...socialForm, platform_email: e.target.value })}
                placeholder="Associated email"
                className="mt-1 bg-[#1E293B] border-slate-700 text-white"
                data-testid="social-email"
              />
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="ghost" onClick={() => setSocialDialogOpen(false)} className="text-slate-400">
                Cancel
              </Button>
              <Button type="submit" className="bg-[#4A90D9] hover:bg-[#3A80C9]" data-testid="submit-social-btn">
                Connect Account
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Payment Methods */}
      <Card className="bg-[#111827] border-white/10">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-white flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-[#4A90D9]" />
              Payment Methods
            </CardTitle>
            <CardDescription className="text-slate-400">
              Manage your saved payment methods
            </CardDescription>
          </div>
          <Dialog open={paymentDialogOpen} onOpenChange={setPaymentDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-[#4A90D9] hover:bg-[#3A80C9]" data-testid="add-payment-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add Card
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#111827] border-white/10">
              <DialogHeader>
                <DialogTitle className="text-white">Add Payment Method</DialogTitle>
                <DialogDescription className="text-slate-400">
                  Add a new card for payments
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleAddPayment} className="space-y-4">
                <div>
                  <Label className="text-slate-300">Card Type</Label>
                  <Select 
                    value={newPayment.card_type} 
                    onValueChange={(value) => setNewPayment({ ...newPayment, card_type: value })}
                  >
                    <SelectTrigger className="mt-1 bg-[#1E293B] border-slate-700 text-white" data-testid="card-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1E293B] border-slate-700">
                      <SelectItem value="visa">Visa</SelectItem>
                      <SelectItem value="mastercard">Mastercard</SelectItem>
                      <SelectItem value="amex">American Express</SelectItem>
                      <SelectItem value="discover">Discover</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-slate-300">Last 4 Digits</Label>
                  <Input
                    value={newPayment.last_four}
                    onChange={(e) => setNewPayment({ ...newPayment, last_four: e.target.value.slice(0, 4) })}
                    placeholder="1234"
                    maxLength={4}
                    required
                    className="mt-1 bg-[#1E293B] border-slate-700 text-white"
                    data-testid="card-last-four"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-slate-300">Expiry Month</Label>
                    <Input
                      value={newPayment.expiry_month}
                      onChange={(e) => setNewPayment({ ...newPayment, expiry_month: e.target.value.slice(0, 2) })}
                      placeholder="MM"
                      maxLength={2}
                      required
                      className="mt-1 bg-[#1E293B] border-slate-700 text-white"
                      data-testid="card-expiry-month"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-300">Expiry Year</Label>
                    <Input
                      value={newPayment.expiry_year}
                      onChange={(e) => setNewPayment({ ...newPayment, expiry_year: e.target.value.slice(0, 4) })}
                      placeholder="YYYY"
                      maxLength={4}
                      required
                      className="mt-1 bg-[#1E293B] border-slate-700 text-white"
                      data-testid="card-expiry-year"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-slate-300">Cardholder Name</Label>
                  <Input
                    value={newPayment.cardholder_name}
                    onChange={(e) => setNewPayment({ ...newPayment, cardholder_name: e.target.value })}
                    placeholder="John Doe"
                    required
                    className="mt-1 bg-[#1E293B] border-slate-700 text-white"
                    data-testid="card-holder-name"
                  />
                </div>
                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="ghost" onClick={() => setPaymentDialogOpen(false)} className="text-slate-400">
                    Cancel
                  </Button>
                  <Button type="submit" className="bg-[#4A90D9] hover:bg-[#3A80C9]" data-testid="submit-payment-btn">
                    Add Card
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </CardHeader>
        <CardContent>
          {paymentMethods.length === 0 ? (
            <div className="text-center py-8">
              <CreditCard className="w-12 h-12 text-slate-600 mx-auto mb-3" />
              <p className="text-slate-400">No payment methods saved</p>
              <p className="text-sm text-slate-500">Add a card to make payments easier</p>
            </div>
          ) : (
            <div className="space-y-3">
              {paymentMethods.map((method) => (
                <div 
                  key={method.id}
                  className="flex items-center justify-between p-4 rounded-lg bg-white/5 border border-white/10"
                  data-testid={`payment-method-${method.id}`}
                >
                  <div className="flex items-center gap-4">
                    {getCardIcon(method.card_type)}
                    <div>
                      <p className="text-white font-medium capitalize">
                        {method.card_type} •••• {method.last_four}
                      </p>
                      <p className="text-sm text-slate-400">
                        {method.cardholder_name} • Expires {method.expiry_month}/{method.expiry_year}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {method.is_default ? (
                      <Badge className="badge-approved">Default</Badge>
                    ) : (
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => handleSetDefault(method.id)}
                        className="text-slate-400 hover:text-white"
                        data-testid={`set-default-${method.id}`}
                      >
                        <Check className="w-4 h-4 mr-1" />
                        Set Default
                      </Button>
                    )}
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => handleDeletePayment(method.id)}
                      className="text-red-400 hover:text-red-300"
                      data-testid={`delete-payment-${method.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
